// Workflow Summary Step - First step to configure analysis preferences
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SummaryBuilder } from '@/components/summary/SummaryBuilder';
import { TemplateSelector } from '@/components/templates';
import { useWorkflow } from '@/contexts/WorkflowContext';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api-client';
import { Sparkles } from 'lucide-react';
import type { SummaryPreferences } from '@creditnova/shared/schemas';

export const Route = createFileRoute('/workflow/_layout/summary')({
  component: SummaryStep,
});


function SummaryStep() {
  const { setCurrentStep, updateData, data, markStepComplete, loadTemplate } = useWorkflow();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  // Ensure workflow context is synced to this step
  useEffect(() => {
    setCurrentStep('summary');
  }, [setCurrentStep]);

  // ---------------------------------------------------------------
  // Auto-apply default template for fresh workflows
  // ---------------------------------------------------------------
  const defaultAppliedRef = useRef(false);

  const { data: templatesData } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.templates.list(),
  });

  useEffect(() => {
    // Only auto-apply once, only on a fresh workflow (no existing config)
    if (defaultAppliedRef.current) return;
    if (!templatesData?.data) return;
    if (data.workflowId || data.templateId || data.summaryPreferences) return;

    const defaultTemplate = templatesData.data.find((t: any) => t.isDefault);
    if (!defaultTemplate) return;

    defaultAppliedRef.current = true;
    loadTemplate(defaultTemplate);
    toast({
      title: 'Default Template Applied',
      description: `Pre-filled with "${defaultTemplate.name}". You can adjust settings before continuing.`,
    });
  }, [templatesData, data.workflowId, data.templateId, data.summaryPreferences, loadTemplate, toast]);

  const handleSavePreferences = async (preferences: SummaryPreferences) => {
    // Block navigation if no document-type data points are selected.
    // Metrics-only doesn't count — the user needs at least one document
    // category to create upload zones on the next page.
    const dp = preferences.selectedDataPoints;
    const hasDocumentTypes =
      (dp.financialStatements?.length ?? 0) > 0 ||
      (dp.creditReport?.length ?? 0) > 0 ||
      (dp.bankStatements?.length ?? 0) > 0 ||
      (dp.corporateSearch?.length ?? 0) > 0;

    if (!hasDocumentTypes) {
      toast({
        title: 'No Document Types Selected',
        description: 'Please select data points for at least one document type before proceeding.',
        variant: 'destructive',
      });
      return;
    }

    if (isSaving) return;
    setIsSaving(true);

    try {
      // ---------------------------------------------------------------
      // Workflow creation / update
      //
      // workflowId is tied to the browser tab (sessionStorage). On the
      // first save we create a new workflow + draft analysis record on
      // the backend. On subsequent saves (e.g., user navigates back to
      // tweak preferences) we PATCH the existing workflow instead.
      // ---------------------------------------------------------------
      if (data.workflowId) {
        // Update existing workflow with new preferences
        await api.workflows.update(data.workflowId, {
          summaryPreferences: preferences,
        });
        updateData({ summaryPreferences: preferences });
      } else {
        // Create new workflow + draft analysis
        const wf = await api.workflows.create({ summaryPreferences: preferences });
        await api.workflows.createAnalysis(wf.id, wf.analysisId, preferences.reportName);
        updateData({
          workflowId: wf.id,
          analysisId: wf.analysisId,
          summaryPreferences: preferences,
        });
      }

      // Mark step as complete
      markStepComplete('summary');

      toast({
        title: 'Preferences Saved',
        description: 'Your summary preferences have been configured. Proceeding to next step.',
      });

      // Navigate to the unified upload page
      navigate({ to: '/workflow/upload', search: { workflowId: undefined } });
    } catch (err: any) {
      console.error('Failed to save workflow:', err);
      toast({
        title: 'Save Failed',
        description: err.message || 'Failed to save preferences. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Sparkles className="w-10 h-10 text-accent-500" />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-accent-500 to-accent-400 bg-clip-text text-transparent">
            Custom Summary Preferences
          </h1>
        </div>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          Configure your executive summary preferences before beginning the analysis.
          Select the data points you want to include and customize the output format.
        </p>

        {/* Template Selector */}
        <div className="mt-6">
          <TemplateSelector 
            onSelect={(template) => {
              loadTemplate(template);
              toast({ title: `Applied ${template.name}` });
            }} 
          />
        </div>
      </div>

      {/* Summary Builder Component */}
      <SummaryBuilder
        initialPreferences={data.summaryPreferences}
        onSave={handleSavePreferences}
        embedded={true}
      />
    </div>
  );
}
